import api from './api';
import {
    AdWallet,
    AdWalletTransaction,
    ViewPackage,
    AdPlacementSlot,
    AdCampaign,
    CampaignAnalytics,
    AdDisplayItem,
    AdDisplayBatchResponse,
    AdCountryRate,
    AdCostEstimation,
} from '../types/adCampaign';

export const adCampaignService = {
    // ==================== Ad Wallet ====================

    async getAdWalletBalance(): Promise<AdWallet> {
        const response = await api.get('/ad-wallet/balance');
        return response.data;
    },

    async createDeposit(amount: number): Promise<{
        client_secret: string;
        payment_intent_id: string;
        amount: number;
        currency: string;
    }> {
        const response = await api.post('/ad-wallet/deposit', { amount });
        return response.data;
    },

    async createDepositCheckout(amount: number, returnPath: string = '/ads/wallet?app=1'): Promise<{
        checkout_session_id: string;
        checkout_url: string;
    }> {
        const response = await api.post('/ad-wallet/deposit/checkout-session', {
            amount,
            return_path: returnPath,
        });
        return response.data;
    },

    async completeDepositCheckout(checkoutSessionId: string): Promise<{
        success: boolean;
        message: string;
        new_balance: number;
    }> {
        const response = await api.post(`/ad-wallet/deposit/checkout-session/${encodeURIComponent(checkoutSessionId)}/complete`);
        return response.data;
    },

    async completeDeposit(paymentIntentId: string, amount?: number): Promise<{
        success: boolean;
        message: string;
        new_balance: number;
    }> {
        const payload: { payment_intent_id: string; amount?: number } = {
            payment_intent_id: paymentIntentId,
        };
        if (typeof amount === 'number' && Number.isFinite(amount) && amount > 0) {
            payload.amount = amount;
        }
        const response = await api.post('/ad-wallet/deposit/complete', payload);
        return response.data;
    },

    async getAdWalletTransactions(
        type?: string,
        limit: number = 50,
        offset: number = 0
    ): Promise<{ transactions: AdWalletTransaction[]; total: number }> {
        const params = new URLSearchParams();
        if (type) params.append('transaction_type', type);
        params.append('limit', limit.toString());
        params.append('offset', offset.toString());
        const response = await api.get(`/ad-wallet/transactions?${params.toString()}`);
        return response.data;
    },

    // ==================== Country Rates & Estimation ====================

    async getCountryRates(region?: string, search?: string): Promise<{
        rates: AdCountryRate[];
        total: number;
    }> {
        const params = new URLSearchParams();
        if (region) params.append('region', region);
        if (search) params.append('search', search);
        const response = await api.get(`/ad-campaigns/country-rates?${params.toString()}`);
        return response.data;
    },

    async getAvailableRegions(): Promise<{ regions: string[] }> {
        const response = await api.get('/ad-campaigns/country-rates/regions');
        return response.data;
    },

    async estimateCost(
        countryCode: string,
        desiredViews: number,
        desiredClicks: number = 0
    ): Promise<AdCostEstimation> {
        const params = new URLSearchParams();
        params.append('country_code', countryCode);
        params.append('desired_views', desiredViews.toString());
        params.append('desired_clicks', desiredClicks.toString());
        const response = await api.post(`/ad-campaigns/estimate?${params.toString()}`);
        return response.data;
    },

    // ==================== View Packages (Legacy) ====================

    async getViewPackages(): Promise<{ packages: ViewPackage[]; total: number }> {
        const response = await api.get('/ad-campaigns/view-packages');
        return response.data;
    },

    // ==================== Placement Slots ====================

    async getPlacementSlots(): Promise<{ slots: AdPlacementSlot[]; total: number }> {
        const response = await api.get('/ad-campaigns/placement-slots');
        return response.data;
    },

    // ==================== Campaign CRUD ====================

    async createCampaign(formData: FormData): Promise<AdCampaign> {
        const response = await api.post('/ad-campaigns/campaigns', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    async getMyCampaigns(
        statusFilter?: string,
        limit: number = 50,
        offset: number = 0
    ): Promise<{ campaigns: AdCampaign[]; total: number }> {
        const params = new URLSearchParams();
        if (statusFilter) params.append('status_filter', statusFilter);
        params.append('limit', limit.toString());
        params.append('offset', offset.toString());
        const response = await api.get(`/ad-campaigns/campaigns?${params.toString()}`);
        return response.data;
    },

    async getCampaign(campaignId: number): Promise<AdCampaign> {
        const response = await api.get(`/ad-campaigns/campaigns/${campaignId}`);
        return response.data;
    },

    async pauseCampaign(campaignId: number): Promise<AdCampaign> {
        const response = await api.post(`/ad-campaigns/campaigns/${campaignId}/pause`);
        return response.data;
    },

    async resumeCampaign(campaignId: number): Promise<AdCampaign> {
        const response = await api.post(`/ad-campaigns/campaigns/${campaignId}/resume`);
        return response.data;
    },

    async cancelCampaign(campaignId: number): Promise<AdCampaign> {
        const response = await api.post(`/ad-campaigns/campaigns/${campaignId}/cancel`);
        return response.data;
    },

    async resubmitCampaign(campaignId: number): Promise<AdCampaign> {
        // Calling PUT on a rejected campaign triggers status -> PENDING_REVIEW on backend.
        // The endpoint is multipart/form-data — an empty FormData is a valid "no-op" payload.
        const formData = new FormData();
        const response = await api.put(`/ad-campaigns/campaigns/${campaignId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    async updateCampaign(campaignId: number, formData: FormData): Promise<AdCampaign> {
        const response = await api.put(`/ad-campaigns/campaigns/${campaignId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    async getCampaignAnalytics(campaignId: number): Promise<CampaignAnalytics> {
        const response = await api.get(`/ad-campaigns/campaigns/${campaignId}/analytics`);
        return response.data;
    },

    // ==================== Ad Display & Tracking ====================

    async getAdsForSlot(slotSlug: string, limit: number = 50): Promise<{
        ads: AdDisplayItem[];
        total: number;
    }> {
        const response = await api.get(`/ad-campaigns/display/${slotSlug}?limit=${limit}`);
        return response.data;
    },

    async getAdsForSlots(slotSlugs: string[], limit: number = 10): Promise<AdDisplayBatchResponse> {
        const params = new URLSearchParams();
        params.append('slots', slotSlugs.join(','));
        params.append('limit', String(limit));
        try {
            const response = await api.get(`/ad-campaigns/display/batch?${params.toString()}`);
            return response.data;
        } catch (err: any) {
            if (err?.response?.status !== 404) {
                throw err;
            }

            const slotResponses = await Promise.all(
                slotSlugs.map(async (slot) => {
                    const response = await this.getAdsForSlot(slot, limit);
                    return [slot, response] as const;
                })
            );
            const slots = Object.fromEntries(slotResponses);
            return {
                slots,
                total: slotResponses.reduce((sum, [, response]) => sum + response.total, 0),
            };
        }
    },

    async trackView(campaignId: number, viewDuration: number = 0): Promise<{
        tracked: boolean;
        unique: boolean;
    }> {
        const response = await api.post('/ad-campaigns/track/view', {
            campaign_id: campaignId,
            view_duration: viewDuration,
        });
        return response.data;
    },

    async trackClick(campaignId: number): Promise<{ tracked: boolean }> {
        const response = await api.post('/ad-campaigns/track/click', {
            campaign_id: campaignId,
        });
        return response.data;
    },
};
